import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import API from "@/lib/api";
import OrgIcon from "@/components/OrgIcon";
import { DT_FALLBACK, IQA_FALLBACK, JNBT_FALLBACK, BLINDSPOT_FALLBACK, NULLSPACE_FALLBACK } from "@/data/leaderboard";

// ── Helpers ─────────────────────────────────────────────────────────────────

const isOursCategory = (cat) => cat && (cat === "ours" || cat.startsWith("ours"));

function dtBadge(row) {
  if (row.category === "ours-research") return "ours · research";
  if (isOursCategory(row.category)) return "ours · released";
  return null;
}

function pct(v) { return `${v}%`; }

export default function BenchmarksPage() {
  const [dtRows, setDtRows] = useState(DT_FALLBACK);
  const [iqaRows, setIqaRows] = useState(IQA_FALLBACK);
  const [jnbtRows, setJnbtRows] = useState(JNBT_FALLBACK);
  const [bsRows, setBsRows] = useState(BLINDSPOT_FALLBACK);
  const [nsRows, setNsRows] = useState(NULLSPACE_FALLBACK);

  useEffect(() => {
    axios.get(`${API}/hf/leaderboard`).then(r => { if (r.data?.rows?.length) setDtRows(r.data.rows); }).catch(() => {});
    axios.get(`${API}/hf/imageqa-leaderboard`).then(r => { if (r.data?.rows?.length) setIqaRows(r.data.rows); }).catch(() => {});
    axios.get(`${API}/hf/nalandabench-leaderboard`).then(r => { if (r.data?.rows?.length) setJnbtRows(r.data.rows); }).catch(() => {});
    axios.get(`${API}/hf/blindspot-leaderboard`).then(r => { if (r.data?.rows?.length) setBsRows(r.data.rows); }).catch(() => {});
    axios.get(`${API}/hf/nullspace-leaderboard`).then(r => { if (r.data?.rows?.length) setNsRows(r.data.rows); }).catch(() => {});
  }, []);

  const dtTop      = dtRows[0];
  const dtFrontier = dtRows.filter(r => r.category === "frontier");
  const dtOurs     = dtRows.filter(r => isOursCategory(r.category));
  const dtTopTeds  = dtTop?.teds ?? 84.9;
  const dtVsBest   = dtOurs[0] && dtFrontier[0] ? (dtOurs[0].teds - dtFrontier[0].teds).toFixed(1) : "7.5";

  const iqaVsBase  = "12.3";

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>Benchmarks — Nalandadata.ai</title>
        <meta name="description" content="The independent benchmark for Indian domain AI. Evaluate model performance on curriculum-grade academic content across K-12, STEM, and higher education." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/benchmarks" />
        <meta property="og:title" content="Benchmarks — Nalandadata.ai" />
        <meta property="og:description" content="The independent benchmark for Indian domain AI. Evaluate model performance on curriculum-grade academic content across K-12, STEM, and higher education." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Benchmarks — Nalandadata.ai" />
        <meta name="twitter:description" content="The independent benchmark for Indian domain AI. Evaluate model performance on curriculum-grade academic content across K-12, STEM, and higher education." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>
      {/* Hero */}
      <section className="s-hero">
        <div className="s-hero-grid" aria-hidden="true" />
        <div className="s-wrap">
          <nav className="s-crumb" aria-label="Breadcrumb" style={{ marginBottom: "18px" }}>
            <Link to="/">Home</Link> / Benchmarks
          </nav>
          <p className="s-eyebrow">Benchmarks</p>
          <h1>The independent benchmark for <span className="hl">Indian domain AI.</span></h1>
          <p className="lede"><b>NalandaBench</b> scores models on verified, curriculum-grade reasoning — expert-authored ground truth with clean IP, built to be reproduced and cited. Independent by design: no lab owns the scorekeeper.</p>
          <p className="s-audience">NalandaBench is the standard evaluation for AI models trained or evaluated on Indian curriculum and domain content. Submit your model to appear on the leaderboard.</p>
          <div className="s-pull-quote">
            <p>A fine-tuned 8B model leads every frontier model on Indian table recognition — <span>+{dtVsBest} points over the best</span> (Claude Sonnet 4.6); the released 7B is right behind. Domain-specific training beats scale.</p>
          </div>
          <div className="s-hero-cta">
            <Link className="s-btn primary" to="/contact">Submit a model →</Link>
            <a className="s-btn ghost" href="https://huggingface.co/Nalandadata" target="_blank" rel="noopener noreferrer">Browse on Hugging Face →</a>
          </div>
        </div>
      </section>

      {/* Why different */}
      <section className="s-band alt">
        <div className="s-wrap">
          <p className="s-eyebrow">Why it's different</p>
          <h2 style={{ fontSize: "clamp(25px,3.6vw,40px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: 0 }}>A measurement only matters if it's neutral.</h2>
          <div className="s-verify">
            <div className="s-vcard">
              <div className="vk">Verified ground truth</div>
              <h3>Expert-authored, not crowd-voted</h3>
              <p>Every item has a verified correct answer from subject-matter experts, ready for verifier-based scoring.</p>
            </div>
            <div className="s-vcard">
              <div className="vk">Independent</div>
              <h3>No lab owns the scorekeeper</h3>
              <p>NalandaBench is built to be the standard others report against — not a tool to validate our own models only.</p>
            </div>
            <div className="s-vcard">
              <div className="vk">Reproducible</div>
              <h3>Public methodology &amp; data</h3>
              <p>Open scoring rules and a public test set on Hugging Face, so any result can be independently checked.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard section — Mercor-style benchmark cards */}
      <section className="s-band" id="leaderboard">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Leaderboard</p>
            <h2>Benchmark results.</h2>
            <p className="lead">Two held-out benchmarks live, with more in progress. Click <b>Read more</b> on any benchmark to explore the full leaderboard, figures, and methodology.</p>
          </div>

          <div className="s-bench-cards">

            {/* ── DrishtiTable card ── */}
            <div className="s-bench-card">
              <div className="s-bench-card-body">
                <div className="s-bench-meta">
                  <div className="s-bench-tag">Table Structure Recognition · TEDS</div>
                  <div className="s-bench-name">DrishtiTable</div>
                  <p className="s-bench-desc">Image → HTML table recognition on {dtTop?.n_samples ?? 135} held-out Indian-textbook tables, ranked by TEDS. A fine-tuned 8B model leads every frontier model by +{dtVsBest} points. Domain-specific training beats scale.</p>
                  <div className="s-bench-links">
                    <Link className="s-bench-link" to="/blog/drishtitable-8b-beats-all-frontier-models">↗ Blog</Link>
                    <span className="s-bench-link" style={{ opacity: 0.4, cursor: "default" }}>Paper — coming</span>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/DrishtiTable" target="_blank" rel="noopener noreferrer">↗ Data</a>
                    <a className="s-bench-link" href="https://github.com/Nalandadata/DrishtiTable" target="_blank" rel="noopener noreferrer">↗ Code</a>
                  </div>
                  <Link className="s-bench-read-more" to="/benchmarks/drishtitable">Read more</Link>
                  <Link className="s-bench-cta" to="/benchmarks/drishtitable">View full leaderboard →</Link>
                </div>

                {/* Mini leaderboard */}
                <div className="s-mini-lb">
                  <div className="s-mini-lb-title">Top results · {dtTop?.n_samples ?? 135} held-out tables</div>
                  <div className="s-mini-rows">
                    {dtRows.slice(0, 5).map(row => (
                      <div key={row.model} className={`s-mini-row${isOursCategory(row.category) ? " ours" : ""}`}>
                        <div className="s-mini-row-top">
                          <OrgIcon org={row.org} className="s-mini-icon" />
                          <span className="s-mini-model">{row.model.replace("DrishtiTable-", "DT-")}</span>
                          <span className="s-mini-score">{row.teds}%</span>
                          {isOursCategory(row.category) && <span className="s-mini-badge">ours</span>}
                        </div>
                        <div className="s-mini-bar-track">
                          <div className="s-mini-bar-fill" style={{ width: `${((row.teds / dtTopTeds) * 100).toFixed(1)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="s-mini-axis"><span>0%</span><span>50%</span><span>100%</span></div>
                </div>
              </div>
            </div>

            {/* ── Nalanda Image VL card ── */}
            <div className="s-bench-card">
              <div className="s-bench-card-body">
                <div className="s-bench-meta">
                  <div className="s-bench-tag">STEM Science QA · Accuracy</div>
                  <div className="s-bench-name">Nalanda Image VL</div>
                  <p className="s-bench-desc">Multiple-choice science reasoning on {162} held-out questions across Physics, Chemistry, Biology and Maths. Fine-tuning LLaMA-3.2-Vision-11B on 22K diagram-grounded pairs yields a +{iqaVsBase}pp gain over the zero-shot base.</p>
                  <div className="s-bench-links">
                    <Link className="s-bench-link" to="/blog/nalanda-image-vl-stem-diagram-finetuning">↗ Blog</Link>
                    <span className="s-bench-link" style={{ opacity: 0.4, cursor: "default" }}>Paper — coming</span>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa" target="_blank" rel="noopener noreferrer">↗ Data</a>
                    <a className="s-bench-link" href="https://github.com/Nalandadata/nalanda-image-vl" target="_blank" rel="noopener noreferrer">↗ Code</a>
                  </div>
                  <Link className="s-bench-read-more" to="/benchmarks/nalanda-image-vl">Read more</Link>
                  <Link className="s-bench-cta" to="/benchmarks/nalanda-image-vl">View full leaderboard →</Link>
                </div>

                {/* Mini leaderboard */}
                <div className="s-mini-lb">
                  <div className="s-mini-lb-title">Top results · {162} held-out MCQs</div>
                  <div className="s-mini-rows">
                    {iqaRows.map(row => (
                      <div key={row.model} className={`s-mini-row${isOursCategory(row.category) ? " ours" : ""}`}>
                        <div className="s-mini-row-top">
                          <OrgIcon org={row.org} className="s-mini-icon" />
                          <span className="s-mini-model">{row.model}</span>
                          <span className="s-mini-score">{row.accuracy}%</span>
                          {row.vs_base != null && <span className="s-mini-badge">+{row.vs_base}pp</span>}
                        </div>
                        <div className="s-mini-bar-track">
                          <div className="s-mini-bar-fill" style={{ width: `${((row.accuracy / 100) * 100).toFixed(1)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="s-mini-axis"><span>0%</span><span>50%</span><span>100%</span></div>
                </div>
              </div>
            </div>

            {/* ── NalandaJEENEET card ── */}
            <div className="s-bench-card">
              <div className="s-bench-card-body">
                <div className="s-bench-meta">
                  <div className="s-bench-tag">Science &amp; Maths Reasoning · JEE / NEET</div>
                  <div className="s-bench-name">NalandaJEENEET</div>
                  <p className="s-bench-desc">{jnbtRows[0]?.n_samples ?? 785} held-out MCQs across Physics, Chemistry, Mathematics and Biology at JEE/NEET difficulty. Verified-reward RL (GRPO) on 116K expert questions outperforms the zero-shot base by +{jnbtRows.find(r => r.category === "fine-tuned")?.vs_base ?? 6.3}pp.</p>
                  <div className="s-bench-links">
                    <Link className="s-bench-link" to="/blog/nalanda-jee-neet-bench-launch">↗ Blog</Link>
                    <span className="s-bench-link" style={{ opacity: 0.4, cursor: "default" }}>Paper — coming</span>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/NalandaJEENEETBench" target="_blank" rel="noopener noreferrer">↗ Data</a>
                    <a className="s-bench-link" href="https://huggingface.co/Nalandadata/nalanda-qwen-7b-grpo" target="_blank" rel="noopener noreferrer">↗ Model</a>
                  </div>
                  <Link className="s-bench-read-more" to="/benchmarks/nalanda-jeeneet">Read more</Link>
                  <Link className="s-bench-cta" to="/benchmarks/nalanda-jeeneet">View full leaderboard →</Link>
                </div>

                {/* Mini leaderboard */}
                <div className="s-mini-lb">
                  <div className="s-mini-lb-title">Top results · {jnbtRows[0]?.n_samples ?? 785} held-out MCQs</div>
                  <div className="s-mini-rows">
                    {jnbtRows.slice(0, 5).map(row => (
                      <div key={row.model} className={`s-mini-row${row.category === "fine-tuned" || isOursCategory(row.category) ? " ours" : ""}`}>
                        <div className="s-mini-row-top">
                          <OrgIcon org={row.org} className="s-mini-icon" />
                          <span className="s-mini-model">{row.model}</span>
                          <span className="s-mini-score">{row.accuracy}%</span>
                          {row.vs_base != null && row.vs_base > 0 && <span className="s-mini-badge">+{row.vs_base}pp</span>}
                        </div>
                        <div className="s-mini-bar-track">
                          <div className="s-mini-bar-fill" style={{ width: `${((row.accuracy / (jnbtRows[0]?.accuracy || 100)) * 100).toFixed(1)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="s-mini-axis"><span>0%</span><span>50%</span><span>100%</span></div>
                </div>
              </div>
            </div>

            {/* ── NULLSPACE card ── */}
            <div className="s-bench-card">
              <div className="s-bench-card-body">
                <div className="s-bench-meta">
                  <div className="s-bench-tag">STEM Reasoning · JEE · Failure-Only</div>
                  <div className="s-bench-name">NULLSPACE</div>
                  <p className="s-bench-desc">267 JEE questions frontier models actually get wrong — every answer key independently confirmed. 43 of 267 failed by all three simultaneously. On uncontaminated rows, Claude Opus 5 still fails a third of them.</p>
                  <div className="s-bench-links">
                    <Link className="s-bench-link" to="/blog/nullspace-jee-benchmark-contamination-tracking">↗ Blog</Link>
                    <span className="s-bench-link" style={{ opacity: 0.4, cursor: "default" }}>Paper — coming</span>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/NULLSPACE" target="_blank" rel="noopener noreferrer">↗ Data</a>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/NULLSPACE-sample" target="_blank" rel="noopener noreferrer">↗ Sample</a>
                  </div>
                  <Link className="s-bench-read-more" to="/benchmarks/nullspace">Read more</Link>
                  <Link className="s-bench-cta" to="/benchmarks/nullspace">View full results →</Link>
                </div>

                {/* Mini leaderboard — accuracy, sorted descending */}
                <div className="s-mini-lb">
                  <div className="s-mini-lb-title">Results · 267 items · Accuracy</div>
                  <div className="s-mini-rows">
                    {nsRows.slice(0, 3).map((row, idx) => (
                      <div key={row.model} className="s-mini-row">
                        <div className="s-mini-row-top">
                          <OrgIcon org={row.org} className="s-mini-icon" />
                          <span className="s-mini-model">{row.model}</span>
                          <span className="s-mini-score">{row.accuracy}%</span>
                          {row.clean_accuracy != null && <span className="s-mini-badge" style={{ color: "#f5a623", background: "rgba(245,166,35,0.12)" }}>{row.clean_accuracy}% clean</span>}
                        </div>
                        <div className="s-mini-bar-track">
                          <div className="s-mini-bar-fill" style={{ width: `${((row.accuracy / nsRows[0].accuracy) * 100).toFixed(1)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="s-mini-axis"><span>0%</span><span>30%</span><span>62%</span></div>
                </div>
              </div>
            </div>

            {/* ── BLINDSPOT card ── */}
            <div className="s-bench-card">
              <div className="s-bench-card-body">
                <div className="s-bench-meta">
                  <div className="s-bench-tag">Diagram Labeling · No-Image Control</div>
                  <div className="s-bench-name">BLINDSPOT</div>
                  <p className="s-bench-desc">Show 9 frontier models an unlabelled biology diagram; they must name every part. Remove the image and run again. 8 of 9 models keep 70–100% of their score — ranking by what the image contributes reverses the leaderboard.</p>
                  <div className="s-bench-links">
                    <Link className="s-bench-link" to="/blog/blindspot-no-image-control-biology-diagrams">↗ Blog</Link>
                    <span className="s-bench-link" style={{ opacity: 0.4, cursor: "default" }}>Paper — coming</span>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/BLINDSPOT" target="_blank" rel="noopener noreferrer">↗ Data</a>
                    <a className="s-bench-link" href="https://huggingface.co/datasets/Nalandadata/BLINDSPOT-sample" target="_blank" rel="noopener noreferrer">↗ Sample</a>
                  </div>
                  <Link className="s-bench-read-more" to="/benchmarks/blindspot">Read more</Link>
                  <Link className="s-bench-cta" to="/benchmarks/blindspot">View full results →</Link>
                </div>

                {/* Mini leaderboard — told score, sorted descending */}
                <div className="s-mini-lb">
                  <div className="s-mini-lb-title">Top results (told) · 181 items · Name-Set F1</div>
                  <div className="s-mini-rows">
                    {bsRows.slice(0, 5).map(row => (
                      <div key={row.model} className="s-mini-row">
                        <div className="s-mini-row-top">
                          <OrgIcon org={row.org} className="s-mini-icon" />
                          <span className="s-mini-model">{row.model}</span>
                          <span className="s-mini-score">{row.told}%</span>
                          {row.grounded != null && <span className="s-mini-badge" style={{ color: "#f5a623", background: "rgba(245,166,35,0.12)" }}>+{row.grounded}pp</span>}
                        </div>
                        <div className="s-mini-bar-track">
                          <div className="s-mini-bar-fill" style={{ width: `${((row.told / bsRows[0].told) * 100).toFixed(1)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="s-mini-axis"><span>0%</span><span>30%</span><span>60%</span></div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="s-band alt s-cs-cta">
        <div className="s-wrap">
          <p className="s-eyebrow" style={{ justifyContent: "center" }}>Get on the leaderboard</p>
          <h2 style={{ fontSize: "clamp(25px,3.6vw,40px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: 0, textAlign: "center" }}>Two ways in.</h2>
          <p style={{ margin: "18px auto 0", maxWidth: "56ch", color: "var(--muted)", textAlign: "center" }}>Depending on whether you want to evaluate your model or license the underlying data.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "20px", maxWidth: "680px", margin: "28px auto 0", textAlign: "left" }}>
            <div style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: "10px", padding: "24px" }}>
              <h3 style={{ fontSize: "13px", fontFamily: "var(--mono)", letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 8px", fontWeight: 800 }}>Submit a model</h3>
              <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 18px", lineHeight: 1.55 }}>Evaluate your model on the held-out test set and appear on the public leaderboard. Open to any model, any lab.</p>
              <Link className="s-btn primary" to="/contact" style={{ width: "100%", textAlign: "center" }}>Submit a model →</Link>
            </div>
            <div style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: "10px", padding: "24px" }}>
              <h3 style={{ fontSize: "13px", fontFamily: "var(--mono)", letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 8px", fontWeight: 800 }}>License the dataset</h3>
              <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 18px", lineHeight: 1.55 }}>Access the full benchmark dataset for training, evaluation, or integration into your own pipeline.</p>
              <Link className="s-btn ghost" to="/contact" style={{ width: "100%", textAlign: "center" }}>License the dataset →</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
